import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { ShoppingCart } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * Admin widget — Recupero Carrelli: quante email di recupero hanno portato
 * a un ordine completato (attribution: ordine della stessa email entro 7gg
 * dall'invio). Fonte: GET /api/dashboard/cart-recovery-stats.
 */
export const CartRecoveryWidget = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = sessionStorage.getItem("access_token");
        const res = await axios.get(`${API}/dashboard/cart-recovery-stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setStats(res.data);
      } catch (err) {
        console.warn("[cart-recovery] stats fetch failed:", err?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const kpi = (label, value, cls = "text-gray-900", testid) => (
    <div className="text-center p-3 bg-gray-50 rounded-lg" data-testid={testid}>
      <div className={`text-2xl font-extrabold ${cls}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-gray-500 mt-1">{label}</div>
    </div>
  );

  return (
    <Card data-testid="cart-recovery-widget">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShoppingCart className="w-5 h-5 text-pink-500" />
          Recupero Carrelli — Ordini Salvati
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-gray-400 text-sm text-center py-4">Caricamento…</p>
        ) : !stats || stats.sent === 0 ? (
          <p className="text-gray-500 text-sm text-center py-4" data-testid="cart-recovery-empty">
            Nessuna email di recupero inviata ancora. I dati appariranno quando un
            cliente lascia il carrello con l'email compilata (invio automatico dopo 1 ora).
          </p>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {kpi("Email inviate", stats.sent, "text-gray-900", "cart-recovery-sent")}
              {kpi("Click sul link", stats.clicked, "text-indigo-600", "cart-recovery-clicked")}
              {kpi("Ordini recuperati", stats.recovered_orders, "text-emerald-600", "cart-recovery-orders")}
              {kpi("Incasso recuperato", `€${(stats.recovered_revenue || 0).toFixed(2)}`, "text-emerald-600", "cart-recovery-revenue")}
            </div>

            <div className="flex items-center gap-2 text-sm" data-testid="cart-recovery-conversion">
              <span className="text-gray-500">Tasso di recupero:</span>
              <div className="flex-1 bg-gray-100 rounded-full h-3">
                <div
                  className="h-3 rounded-full bg-gradient-to-r from-pink-500 to-emerald-500 transition-all duration-500"
                  style={{ width: `${Math.min(Math.max(stats.conversion_percent, 2), 100)}%` }}
                ></div>
              </div>
              <span className="font-bold text-gray-900">{stats.conversion_percent}%</span>
            </div>

            {stats.variants?.length > 0 && (
              <div className="flex flex-wrap gap-2 text-xs">
                {stats.variants.map((v) => (
                  <Badge key={v.variant} variant="secondary" className="font-normal" data-testid={`cart-recovery-variant-${v.variant}`}>
                    {v.variant.toUpperCase()}{v.variant === "v1" ? " (coupon)" : v.variant === "v2" ? " (urgenza)" : ""}:{" "}
                    {v.recovered}/{v.sent} recuperati · €{(v.revenue || 0).toFixed(2)}
                  </Badge>
                ))}
              </div>
            )}

            {stats.recent?.length > 0 && (
              <div className="pt-3 border-t border-gray-200">
                <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-2">Ultimi invii</p>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {stats.recent.map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-xs gap-2" data-testid={`cart-recovery-recent-${i}`}>
                      <span className="text-gray-600 truncate">{r.email}</span>
                      <span className="flex items-center gap-1.5 shrink-0">
                        <span className="text-gray-400">{(r.sent_at || "").slice(0, 10)}</span>
                        {r.clicked && <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 px-1.5 py-0">click</Badge>}
                        {r.recovered ? (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 px-1.5 py-0">
                            ✓ ordine €{(r.order_total || 0).toFixed(0)}
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-500 hover:bg-gray-100 px-1.5 py-0">in attesa</Badge>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
